// src/hooks/useOpsNotifications.js
//
// Single source of truth for the ops-priority notifications shown in the
// top navbar's bell dropdown. The hook is split into two layers:
//   1. `useOpsNotificationsData` — pulls a small recent-orders + drivers
//      slice and reads inquiries from Redux. This runs once per Header
//      mount so the navbar always has fresh-enough numbers without
//      duplicating each screen's heavier fetches.
//   2. `useOpsNotifications` — pure compute layer. Given orders, drivers
//      and inquiries it returns the sorted action queue used by the
//      dropdown. It is exported separately so other surfaces (Command
//      Center, Dispatch) could reuse the same prioritisation later.
//
// Routes deliberately deep-link into Dispatch with `filter=…` so the
// existing quick-filter logic in DispatchScreen kicks in (mirrors the
// `openDispatchWith` helper on Command Center).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  isOrderWithinNextDaysPST,
  isTerminalOrderStatus,
} from '../components/commandCenter/opsStats';
import {
  isDoorDeliveryOrder,
  isDoorDeliveryDispatched,
} from '../utils/dispatchOrderBuckets';
import { hoursSince } from '../components/inquiriesComponents/workspace/utils';
import { useResolvedOrderCoords } from '../utils/resolveOrderCoords';
import { fetchInquiries } from '../store/thunks/inquiriesActions';
import request from '../utils/request';

const MILES_PER_KM = 0.621371;
const PROX_THRESHOLD_KM = 16;
const DRIVER_SPARSE_THRESHOLD = 3;
const SCARCITY_EXCLUDED_STATUSES = new Set(['completed', 'created', 'pending']);

const NAV_WINDOW_DAYS = 3;
const ORDER_FETCH_LIMIT = 200;
const ORDER_FETCH_MAX = 600;
const REFRESH_INTERVAL_MS = 90_000;

function haversineKmFast(a, b) {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// How many calendar days from "today PST" until the order's in-stock date.
// Returns Infinity for missing dates (treated as not-urgent rather than
// most-urgent so we don't over-pulse).
function daysUntilInStock(order) {
  const inStock = order?.inStockDate;
  if (!inStock) return Infinity;
  const target = new Date(inStock);
  if (Number.isNaN(target.getTime())) return Infinity;
  const now = new Date();
  const ms = target.getTime() - now.getTime();
  return ms / 86_400_000;
}

// Worst-per-bucket urgency for a list of orders.
//   super  → any order ≤ 1d away
//   medium → any order < 2d away
//   urgent → any order ≤ 3d away
//   none   → otherwise
function orderListUrgency(list) {
  if (!list || list.length === 0) return 'none';
  let worst = 'none';
  for (const o of list) {
    const d = daysUntilInStock(o);
    if (d <= 1) return 'super';
    if (d < 2 && worst !== 'super') worst = 'medium';
    else if (d <= 3 && worst === 'none') worst = 'urgent';
  }
  return worst;
}

// Visual tone per (action × urgency). Mirrors the OpsStatCard tone keys so
// dark-mode + ring styling already work the same way.
function toneFor(actionId, urgency) {
  if (urgency === 'super') {
    if (actionId === 'IMMEDIATE_RESPONSE_NEEDED') return 'red';
    return 'rose';
  }
  if (urgency === 'urgent') return 'emerald';
  switch (actionId) {
    case 'POST_TASK_GIG':
      return 'rose';
    case 'START_DRIVER_OUTREACH':
      return 'violet';
    case 'PLACE_ORDER':
      return 'amber';
    case 'REPLY_TO_INQUIRY':
      return 'indigo';
    case 'IMMEDIATE_RESPONSE_NEEDED':
      return 'red';
    default:
      return 'gray';
  }
}

const URGENCY_RANK = { super: 0, medium: 1, urgent: 2, none: 3 };

// Pure compute hook. Accepts already-fetched data so Command Center /
// Dispatch can pass their own state in if we later want to mount the
// dropdown without re-fetching.
export function useOpsNotifications({
  orders = [],
  drivers = [],
  inquiries = [],
} = {}) {
  // Active, in-window orders we use for all order-related counts. Mirrors
  // CommandCenterScreen's `pendingForScarcity` so the bell agrees with the
  // big stats row.
  const activeInWindow = useMemo(() => {
    return (orders || []).filter((o) => {
      if (!o) return false;
      if (isTerminalOrderStatus(o.status)) return false;
      if (!isOrderWithinNextDaysPST(o, NAV_WINDOW_DAYS)) return false;
      return true;
    });
  }, [orders]);

  const scarcityPool = useMemo(() => {
    return activeInWindow.filter(
      (o) => !SCARCITY_EXCLUDED_STATUSES.has(o.status)
    );
  }, [activeInWindow]);

  const resolvedOrderCoords = useResolvedOrderCoords(scarcityPool);

  const driversWithLatLng = useMemo(() => {
    return (drivers || [])
      .map((d) => {
        const lat = Number(d?.currentAddress?.lat);
        const lng = Number(d?.currentAddress?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const rangeMi = Number(d?.deliveryRange);
        return {
          id: d.id || d._id,
          coord: { lat, lng },
          rangeMi:
            Number.isFinite(rangeMi) && rangeMi > 0 ? rangeMi : null,
        };
      })
      .filter(Boolean);
  }, [drivers]);

  // POST TASK/GIG — orders with <3 drivers in delivery range.
  const driverSparseOrders = useMemo(() => {
    if (driversWithLatLng.length === 0) return [];
    const out = [];
    scarcityPool.forEach((order) => {
      const coord = resolvedOrderCoords[order._id];
      if (!coord) return;
      let nearby = 0;
      for (const d of driversWithLatLng) {
        const km = haversineKmFast(d.coord, coord);
        const distMi = km * MILES_PER_KM;
        const maxMi =
          d.rangeMi != null ? d.rangeMi : PROX_THRESHOLD_KM * MILES_PER_KM;
        if (distMi <= maxMi) {
          nearby += 1;
          if (nearby >= DRIVER_SPARSE_THRESHOLD) break;
        }
      }
      if (nearby < DRIVER_SPARSE_THRESHOLD) out.push(order);
    });
    return out;
  }, [scarcityPool, resolvedOrderCoords, driversWithLatLng]);

  // START DRIVER OUTREACH — active orders with no assigned driver.
  // Door-delivery orders intentionally have no driver, so we exclude them.
  const noDriverOrders = useMemo(() => {
    return activeInWindow.filter(
      (o) => !o.driverID && !isDoorDeliveryOrder(o)
    );
  }, [activeInWindow]);

  // PLACE ORDER — no store reference yet AND no spend yet. Door orders
  // already saved as dispatched (door-aware bucket) are excluded.
  const unplacedOrders = useMemo(() => {
    return activeInWindow.filter((o) => {
      if (isDoorDeliveryDispatched(o)) return false;
      if (
        o.storeOrderId ||
        (o.storeOrderConfirmationUrl && o.storeOrderConfirmationUrl.trim())
      )
        return false;
      if (o.storeSpend && Number(o.storeSpend) > 0) return false;
      return true;
    });
  }, [activeInWindow]);

  // Inquiry buckets, mirroring the constants from inquiriesComponents.
  const { inquiriesAwaiting, inquiriesOverdue } = useMemo(() => {
    const awaiting = [];
    const overdue = [];
    (inquiries || []).forEach((i) => {
      if (!i || i.closed || i.replied) return;
      const h = hoursSince(i.createdAt);
      if (h >= 1) overdue.push(i);
      else awaiting.push(i);
    });
    return { inquiriesAwaiting: awaiting, inquiriesOverdue: overdue };
  }, [inquiries]);

  const items = useMemo(() => {
    const list = [];

    // Driver sparse → POST TASK/GIG
    {
      const count = driverSparseOrders.length;
      const urgency = orderListUrgency(driverSparseOrders);
      list.push({
        id: 'POST_TASK_GIG',
        label: 'POST TASK/GIG',
        helperText: 'Low driver coverage near delivery area',
        cta: 'Post task/gig',
        count,
        urgency: count === 0 ? 'none' : urgency,
        tone: toneFor('POST_TASK_GIG', count === 0 ? 'none' : urgency),
        iconName: 'mapPin',
        route: '/dispatch?filter=driver-sparse',
      });
    }

    // No driver → START DRIVER OUTREACH
    {
      const count = noDriverOrders.length;
      const urgency = orderListUrgency(noDriverOrders);
      list.push({
        id: 'START_DRIVER_OUTREACH',
        label: 'START DRIVER OUTREACH',
        helperText: 'Drivers nearby, none accepted yet',
        cta: 'Start outreach',
        count,
        urgency: count === 0 ? 'none' : urgency,
        tone: toneFor(
          'START_DRIVER_OUTREACH',
          count === 0 ? 'none' : urgency
        ),
        iconName: 'truck',
        route: '/dispatch?filter=no-driver',
      });
    }

    // Unplaced → PLACE ORDER
    {
      const count = unplacedOrders.length;
      const urgency = orderListUrgency(unplacedOrders);
      list.push({
        id: 'PLACE_ORDER',
        label: 'PLACE ORDER',
        helperText: 'No store order ID yet',
        cta: 'Place order',
        count,
        urgency: count === 0 ? 'none' : urgency,
        tone: toneFor('PLACE_ORDER', count === 0 ? 'none' : urgency),
        iconName: 'help',
        route: '/dispatch?filter=unplaced',
      });
    }

    // Overdue inquiries → IMMEDIATE RESPONSE NEEDED (always super-urgent
    // when count > 0; that's the whole point of the >1h threshold).
    {
      const count = inquiriesOverdue.length;
      list.push({
        id: 'IMMEDIATE_RESPONSE_NEEDED',
        label: 'IMMEDIATE RESPONSE NEEDED',
        helperText: 'Open longer than 1 hour',
        cta: 'Reply now',
        count,
        urgency: count === 0 ? 'none' : 'super',
        tone: toneFor(
          'IMMEDIATE_RESPONSE_NEEDED',
          count === 0 ? 'none' : 'super'
        ),
        iconName: 'inbox',
        route: '/inquiries?urgency=overdue',
      });
    }

    // New / awaiting inquiries → REPLY TO INQUIRY (medium when there are any).
    {
      const count = inquiriesAwaiting.length;
      list.push({
        id: 'REPLY_TO_INQUIRY',
        label: 'REPLY TO INQUIRY',
        helperText: 'Awaiting team reply',
        cta: 'Reply to inquiry',
        count,
        urgency: count === 0 ? 'none' : 'medium',
        tone: toneFor(
          'REPLY_TO_INQUIRY',
          count === 0 ? 'none' : 'medium'
        ),
        iconName: 'inbox',
        route: '/inquiries',
      });
    }

    list.sort((a, b) => {
      const ua = URGENCY_RANK[a.urgency] ?? 99;
      const ub = URGENCY_RANK[b.urgency] ?? 99;
      if (ua !== ub) return ua - ub;
      return b.count - a.count;
    });

    return list;
  }, [
    driverSparseOrders,
    noDriverOrders,
    unplacedOrders,
    inquiriesAwaiting,
    inquiriesOverdue,
  ]);

  const totalActionable = useMemo(
    () =>
      items.reduce(
        (sum, it) =>
          it.urgency === 'super' || it.urgency === 'medium'
            ? sum + it.count
            : sum,
        0
      ),
    [items]
  );

  const hasSuperUrgent = useMemo(
    () => items.some((it) => it.urgency === 'super' && it.count > 0),
    [items]
  );

  return { items, totalActionable, hasSuperUrgent };
}

// Lightweight self-fetching wrapper used by the navbar bell. Loads a small
// active-orders + drivers slice and ensures inquiries are in Redux. Does
// NOT contend with screens that load their own larger data sets — both
// fetches go through the same backend that already serves Command Center
// and Dispatch.
export function useOpsNotificationsData() {
  const dispatch = useDispatch();
  const inquiries = useSelector((s) => s.inquiries?.list || []);
  const isAuthenticated = useSelector((s) => Boolean(s.login?.token));

  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const ordersPromise = (async () => {
        let all = [];
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
          const params = {
            status: 'pending,requested,picked,paymentCompleted',
            limit: ORDER_FETCH_LIMIT,
          };
          if (skip > 0) params.skip = skip;
          const { data: batch = [] } = await request.get('/orders', {
            params,
          });
          if (!Array.isArray(batch) || batch.length === 0) break;
          all = all.concat(batch);
          if (batch.length < ORDER_FETCH_LIMIT) hasMore = false;
          else skip += ORDER_FETCH_LIMIT;
          if (all.length >= ORDER_FETCH_MAX) break;
        }
        return all;
      })();

      const driversPromise = request
        .get('/drivers')
        .then((res) => (Array.isArray(res?.data) ? res.data : []))
        .catch(() => []);

      const [nextOrders, nextDrivers] = await Promise.all([
        ordersPromise.catch(() => []),
        driversPromise,
      ]);
      setOrders(nextOrders);
      setDrivers(nextDrivers);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    refresh();
    dispatch(fetchInquiries());
    const id = window.setInterval(() => {
      refresh();
      dispatch(fetchInquiries());
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isAuthenticated, refresh, dispatch]);

  const computed = useOpsNotifications({ orders, drivers, inquiries });

  return { ...computed, loading, refresh };
}

export default useOpsNotifications;
